import { useFieldContext } from "./FieldContext";
import { Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { getFileExtension, resolvePath } from "../utils";
import { useEffect, useRef, useState } from "react";
import useEventListener from "../use-event-listener";
import useModal from "../UseModal";

/**
 * @param {Object} props
 * @param {boolean} props.required
 * @param {string} props.label
 * @param {string} props.id
 * @param {number} props.maxFileSizeMB
 * @param {string[]} props.extensions
 * @param {(file: File) => Promise<string> | string | undefined} props.validate
 */
export default function FileInput({ required, label, id, maxFileSizeMB = 5, extensions, validate, children, ...rest }) {
    extensions = extensions.map(ext => ext.toLowerCase());
    const context = useFieldContext();
    id = context.idPrefix + id;

    const { register, unregister, setValue, watch, trigger, formState: { errors, isSubmitting, isSubmitSuccessful, disabled: formDisabled }, custom: { noAsterisk } } = useFormContext();
    const error = id && resolvePath(errors, id);
    register(id, { ...rest, required });
    rest.disabled ||= context.disabled || formDisabled;

    const dropArea = useRef();
    const { ModalDialog, showAlert } = useModal();
    const [draggingOver, setDraggingOver] = useState(false);

    useEffect(() => () => {
        unregister(id, { keepValue: true });
    }, []);

    useEventListener(window, "drop", e => {
        e.preventDefault();
        setDraggingOver(false);
    });
    useEventListener(window, "dragleave", e => {
        if (e.target === dropArea.current) {
            setDraggingOver(false);
        }
    });
    useEventListener(window, "dragover", e => {
        e.preventDefault();
        if (!dropArea.current.contains(e.target)) {
            e.dataTransfer.dropEffect = "none";
            setDraggingOver(false);
        }
    });

    function onClick() {
        if (isSubmitting || rest.disabled) {
            return;
        }

        const input = document.createElement('input');
        input.type = "file";
        if (extensions) {
            input.accept = extensions.map(ext => `.${ext}`).join();
        }
        input.click();
        input.oncancel = () => input.remove();
        input.onchange = async () => {
            await readFileList(input.files);
            input.remove();
        };
    }

    async function readFileList(fileList) {
        if (isSubmitting || rest.disabled) {
            return;
        }

        const file = fileList[0];

        if (extensions && !extensions.includes(getFileExtension(file.name).toLowerCase())) {
            await showAlert("Error", `The selected file is of the wrong type (only ${extensions.map(ext => ext.toUpperCase()).join(", ")} files are allowed).`);
            return;
        }

        if (file.size > maxFileSizeMB * 1024 * 1024) {
            await showAlert("Error", `The selected file is larger than the maximum size (${maxFileSizeMB} MB).`);
            return;
        }

        const error = await validate?.(file);
        if (error) {
            await showAlert("Error", error);
            return;
        }

        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
            setValue(id, { name: file.name, data: fileReader.result });
            trigger(id);
        };
    }

    function clearFile(e) {
        if (isSubmitting) {
            return;
        }

        e.stopPropagation();
        setValue(id, null);

        if (isSubmitSuccessful) {
            trigger(id);
        }
    }

    function onDragOver(e) {
        if (isSubmitting || rest.disabled) {
            return;
        }

        e.preventDefault();

        if (isSubmitting) {
            e.dataTransfer.dropEffect = "none";
            return;
        }

        e.dataTransfer.dropEffect = "copy";
        setDraggingOver(true);
    }

    return (
        <Form.Group className="form-group w-full">
            {ModalDialog}
            {label && <Form.Label className="text-nowrap text-sm font-medium text-neutral-600 mb-2">{label}{required ? !noAsterisk && "*" : <i className="font-normal"> (optional)</i>}</Form.Label>}
            <div ref={dropArea} className={`file-input ${draggingOver ? "dragging-over" : ""} ${watch(id) ? "has-file" : ""} ${rest.disabled ? "disabled" : ""}`} onClick={onClick} onDragOver={onDragOver} onDrop={e => readFileList(e.dataTransfer.files)}>
                <i className={`bi ${watch(id) ? "bi-file-earmark-arrow-up" : "bi-upload"} fs-1`} />
                <div className="d-flex align-items-center justify-content-center">
                    <span className={`min-w-full ${watch(id) ? "text-truncate" : ""}`} style={{ color: "black", fontWeight: 600 }}>{watch(id) ? watch(id).name : "Drag & drop here or click to browse"}</span>
                    {watch(id) && <i className="bi bi-x hover-red" onClick={clearFile} />}
                </div>
                <Form.Text className="text-center mt-0">Maximum file size: {maxFileSizeMB} MB</Form.Text>
                <Form.Text className="text-center mt-0">Allowed file types: {extensions.map(e => e.toUpperCase()).join(", ")}</Form.Text>
            </div>
            {error && <Form.Control.Feedback type="invalid" className="d-block">No file selected.</Form.Control.Feedback>}
            {children}
        </Form.Group>
    );
}